// Actions the page on screen adds to the ⌘K palette, such as a record page's
// "Delete this account" or "Edit Industry". The shell lists them first and
// drops them when the page goes away.
import type { CommandItem } from '@olytic/foundation';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

type SetActions = (actions: CommandItem[]) => void;

const PageActionsContext = createContext<{ actions: CommandItem[]; setActions: SetActions }>({
  actions: [],
  setActions: () => {},
});

export function PageActionsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [actions, setActions] = useState<CommandItem[]>([]);
  return <PageActionsContext.Provider value={{ actions, setActions }}>{children}</PageActionsContext.Provider>;
}

/** The current page's actions, for the palette. */
export function usePageActionItems(): CommandItem[] {
  return useContext(PageActionsContext).actions;
}

/**
 * Offers `actions` in the palette while the calling page is mounted. Pass a
 * memoized list: a new list replaces the old one.
 */
export function usePageActions(actions: CommandItem[]): void {
  const { setActions } = useContext(PageActionsContext);
  useEffect(() => {
    setActions(actions);
    return () => setActions([]);
  }, [actions, setActions]);
}
